import Config from "@/config/config.js";
import AsyncUtils from "@/utils/asyncUtils.js";
import backendService from "./index.service.js";
import Services, { handleError } from "./index.service";
import { getSession } from "@/hooks/useSession.js";
import AuthService from "@services/auth.service.js";
import State, { STATE, InvitationState } from "@state";
import { sha256 } from "@/utils/crypto.js";

const { OshimedBackend, OshimedAPI } = Services;

/**
 * Fetch entities for a given user id and type.
 * @param {object} params
 * @param {string} params.userId - UUID of the user
 * @param {string} params.entityType - Type of entity (e.g. "cro")
 * @returns {Promise<object|array>} Array of entities or error object
 */
const getEntitiesByUserId = async ({ userId, entityType = "cro" }) => {
  try {
    if (!userId) {
      return {
        success: false,
        message: "Missing userId",
        errorCode: "entity-no-user-id",
        errorKey: 667201,
      };
    }

    const url = `/entity/entities/${userId}?type=${entityType}`;
    const response = await OshimedBackend.get(url);
    return response.data;
  } catch (error) {
    return handleError(error, 667202);
  }
};

/**
 * Fetches entity information for a given user ID.
 * @param {string} entityId - The ID of the entity.
 * @returns {Promise<object>} Success or error object.
 */
const getEntityById = async (entityId) => {
  // Changed parameter to entityId
  console.log(
    "=== Service: getEntityById called with ID === entity.service.js === key: 345678 ==="
  );
  console.dir(entityId, { depth: null, colors: true });
  console.log("=================================");
  try {
    const url = `${Config.BACKEND_URL}/entity/${entityId}`; // Changed URL to use entityId
    console.log("Request URL:", url);
    const response = await OshimedBackend.get(url);
    console.log(
      "=== Service: API response === entity.service.js === key: 901234 ==="
    );
    console.dir(response.data, { depth: null, colors: true });
    console.log("=================================");
    if (!response.data) {
      console.warn("API returned empty response");
      return null;
    }
    return response.data;
  } catch (error) {
    console.error("Service error in getEntityById:", error);
    console.error("Error details:", error.response?.data || error.message);
    return handleError(error, 456789);
  }
};

/**
 * Registers an investigator by sending data to the backend.
 * @param {object} params
 * @param {string} params.userId - The ID of the user (investigator).
 * @param {string} params.siteId - The ID of the site the investigator is associated with.
 * @param {string} [params.croEntityId] - The ID of the CRO entity the investigator belongs to (optional).
 * @returns {Promise<object>} Success or error object from the backend.
 */
const registerInvestigator = async ({ userId, siteId, croEntityId }) => {
  try {
    if (!userId || !siteId || !croEntityId) {
      return {
        success: false,
        message:
          "Missing userId or siteId or entityId for investigator registration.",
        errorCode: "investigator-registration-missing-fields-frontend",
        errorKey: 667205,
      };
    }

    // Get the platformToken from the session
    const session = getSession();
    const platformToken = session?.platformToken;

    if (!platformToken) {
      console.error(
        "V0_DEBUG: Missing platformToken in session for registerInvestigator."
      );
      return {
        success: false,
        message:
          "Missing platformToken in session for investigator registration.",
        errorCode: "investigator-registration-no-platform-token",
        errorKey: 667207,
      };
    }

    const url = `entity/investigator/register`;
    const response = await OshimedBackend.post(
      url,
      { userId, siteId, croEntityId },
      {
        headers: {
          "x-webhook-token": platformToken,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Frontend Service error in registerInvestigator:", error);
    return handleError(error, 667206);
  }
};

const joinCro = async () => {
  try {
    const session = getSession();
    const platformToken = session?.platformToken;
    const apiToken = session?.apiToken;
    if (!platformToken) {
      console.error("V0_DEBUG: Missing platformToken in session for joinCro.");
      return {
        success: false,
        message: "Missing platformToken in session for joinCro.",
        errorCode: "join-cro-no-platform-token",
        errorKey: 667208,
      };
    }

    const hashedEmail = await sha256(session.user.email);
    const tmpKeyRes = await AuthService.getTmpApiKey(hashedEmail);

    if (tmpKeyRes?.success === false) {
      console.error("V0_DEBUG: Missing xApiKey in session for joinCro.");
      return {
        success: false,
        message: "Missing xApiKey in session for joinCro.",
        errorCode: "join-cro-no-x-api-key",
        errorKey: 103517,
      };
    }
    const xApiKey = tmpKeyRes;

    console.log("=== xApiKey === entity.service.js === key: 467264 ===");
    console.dir(xApiKey, { depth: null, colors: true });
    console.log("=================================");

    const delegate = STATE[InvitationState.INVITATION_CONTENT].delegate;
    const studyId =
      STATE[InvitationState.INVITATION_CONTENT].studyId ||
      STATE[InvitationState.INVITATION_CONTENT].study_id;

    console.log("=== delegate === entity.service.js === key: 666232 ===");
    console.dir(delegate, { depth: null, colors: true });
    console.log("=================================");

    try {
      const response = await OshimedAPI.patch("/entity/join", {...delegate, studyId}, {
        headers: {
          "x-api-key": xApiKey,
          Authorization: `Bearer ${session.apiToken}`,
        },
      });

      if (!response.data) {
        return {
          success: false,
          message: "Error joining Cro",
          fromError: response,
          errorCode: "join-cro-error",
          errorKey: 964401,
        };
      }

      return response.data;
    } catch (error) {
      console.log("=== error === entity.service.js === key: 575604 ===");
      console.dir(error, { depth: null, colors: true });
      console.log("=================================");
      return handleError(error, 815814);
    }
  } catch (error) {
    console.log("=== error === entity.service.js === key: 592349 ===");
    console.dir(error, { depth: null, colors: true });
    console.log("=================================");
    return handleError(error, 780962);
  }
};

const addAdmin = async ({ entityId, userId, accepted = true }) => {
  try {
    if (!entityId || !userId) {
      return {
        success: false,
        message: "Missing entityId or userId for addAdmin.",
        errorCode: "add-admin-missing-fields-frontend",
        errorKey: 667209,
      };
    }

    const session = getSession();
    const { platformToken } = session || {};

    if (!platformToken) {
      return {
        success: false,
        message: "Missing platformToken in session for addAdmin.",
        errorCode: "add-admin-no-api-token",
        errorKey: 667210,
      };
    }

    const response = await OshimedBackend.post(
      "/entity/add-admin",
      {
        entityId,
        userId,
        accepted,
      },
      {
        headers: {
          Authorization: `Bearer ${platformToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Frontend Service error in addAdmin:", error);
    return handleError(error, 667211);
  }
};

/**
 * Met à jour les informations de contact d'une entité.
 * Utilise OshimedBackend et retourne directement la réponse.
 * @param {string} entityId – L’UUID de l’entité à mettre à jour.
 * @param {object} contact – Les nouvelles informations de contact (structure JSONB).
 * @returns {Promise<object>} Résultat de la mise à jour ou erreur.
 */
const updateEntityById = async (entityId, contact) => {
  try {
    if (!entityId || !contact) {
      return {
        success: false,
        message: "Champs manquants: entityId ou contact",
        errorCode: "update-entity-missing-fields",
        errorKey: 667203,
      };
    }

    const response = await OshimedBackend.put(`/entity/${entityId}`, contact);
    return response.data;
  } catch (error) {
    return handleError(error, 667204);
  }
};

const EntityService = {
  getEntityById,
  getEntitiesByUserId,
  registerInvestigator,
  joinCro,
  addAdmin,
  updateEntityById,
};

export default EntityService;
